// src/components/modals/AdoptionRequestsModal.jsx
"use client";

import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { adoptionAPI } from '../../services/adoptionAPI';
import { getUserPlaceholderImage } from '../../utils/imageUtils';
import { handleApiError } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { getUserTypeColor } from '../../utils/userTypeUtils';
import AdoptionPostDetailModal from './AdoptionPostDetailModal';
import './AdoptionRequestsModal.css';

const AdoptionRequestsModal = ({
  isOpen,
  onClose,
  onRequestAccepted,
  onRequestRejected,
  highlightedRequestId,
  postId = null, // Optional: filter requests by post ID
  currentUser = null // Current user for post detail modal
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.dir() === 'rtl';
  const { user, getUserType } = useAuth();

  // Portal-mounted modal doesn't inherit the page's --adoption-accent
  const userTypeColor = getUserTypeColor(getUserType());

  const [activeTab, setActiveTab] = useState('received'); // 'received' or 'made'
  const [requests, setRequests] = useState([]);
  const [madeRequests, setMadeRequests] = useState([]);
  const [historyRequests, setHistoryRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clearingHistory, setClearingHistory] = useState(false);
  const [error, setError] = useState(null);
  const [acceptingRequestId, setAcceptingRequestId] = useState(null);
  const [rejectingRequestId, setRejectingRequestId] = useState(null);
  const [cancelingRequestId, setCancelingRequestId] = useState(null);
  const [showPostModal, setShowPostModal] = useState(false);
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [confirmRequestId, setConfirmRequestId] = useState(null);
  const [fetchedTabs, setFetchedTabs] = useState(new Set());
  const scrollLockRef = useRef(0);

  // Fetch adoption requests
  useEffect(() => {
    if (isOpen) {
      // Always reset fetched tabs on fresh open to ensure fresh data
      setFetchedTabs(new Set());
      fetchRequests('received');
    } else {
      setRequests([]);
      setMadeRequests([]);
      setHistoryRequests([]);
      setFetchedTabs(new Set());
      setError(null);
    }
  }, [isOpen, postId]);

  // Fetch data when active tab changes if not already fetched
  useEffect(() => {
    if (isOpen && !fetchedTabs.has(activeTab)) {
      fetchRequests(activeTab);
    }
  }, [activeTab, isOpen, postId]);

  // Reset to received tab when modal opens or postId changes
  useEffect(() => {
    if (isOpen) {
      setActiveTab('received');
    }
  }, [isOpen, postId]);

  // Scroll to highlighted request when modal opens and requests are loaded
  useEffect(() => {
    if (isOpen && highlightedRequestId && requests.length > 0) {
      const timer = setTimeout(() => {
        const highlightedElement = document.querySelector('.adoption-request-highlighted');
        if (highlightedElement) {
          highlightedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, highlightedRequestId, requests.length]);

  // Handle keyboard events and body scroll without losing page scroll position
  useEffect(() => {
    if (!isOpen) return;

    scrollLockRef.current = window.scrollY || window.pageYOffset || 0;

    const originalStyle = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollLockRef.current}px`;
    document.body.style.width = '100%';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = originalStyle.overflow;
      document.body.style.position = originalStyle.position;
      document.body.style.top = originalStyle.top;
      document.body.style.width = originalStyle.width;
      window.scrollTo({ top: scrollLockRef.current || 0, behavior: "instant" });
    };
  }, [isOpen]);

  const fetchRequests = async (tabToFetch = activeTab) => {
    try {
      setLoading(true);
      setError(null);
      const currentUserId = user?.id || user?.user_id;

      if (!currentUserId) {
        setRequests([]);
        setMadeRequests([]);
        setHistoryRequests([]);
        return;
      }

      const commonParams = postId ? { post_id: postId } : {};
      
      let response;
      if (tabToFetch === 'received') {
        response = await adoptionAPI.getAdoptionRequests({ type: 'received', status: 'pending', ...commonParams });
      } else if (tabToFetch === 'made') {
        response = await adoptionAPI.getAdoptionRequests({ type: 'made', status: 'pending', ...commonParams });
      } else if (tabToFetch === 'history') {
        response = await adoptionAPI.getAdoptionRequests({ type: 'all', status: 'history', ...commonParams });
      }

      if (!response) return;

      const extractData = (res) => {
        if (res.data?.status === 'success' && res.data?.data) {
          if (Array.isArray(res.data.data.data)) return res.data.data.data;
          if (Array.isArray(res.data.data.requests)) return res.data.data.requests;
          if (Array.isArray(res.data.data)) return res.data.data;
        }
        return Array.isArray(res.data) ? res.data : [];
      };

      const data = extractData(response);

      if (tabToFetch === 'received') setRequests(data);
      else if (tabToFetch === 'made') setMadeRequests(data);
      else if (tabToFetch === 'history') setHistoryRequests(data);

      // Mark tab as fetched
      setFetchedTabs(prev => new Set([...prev, tabToFetch]));
    } catch (err) {
      console.error(`Error fetching adoption requests for ${tabToFetch}:`, err);
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && !loading) {
      onClose();
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !loading) {
      onClose();
    }
  };

  const handleViewProfile = (requesterId) => {
    navigate(`/profile/${requesterId}`);
    onClose();
  };

  const handleStartChat = (requesterId, requestId) => {
    // Navigate to chat with the requester using social chat
    // Pass the request ID as a query parameter so we can navigate back
    // This matches the route pattern /chat/:conversationId/:conversationType
    // where conversationId is treated as receiverId for social chats
    navigate(`/chat/${requesterId}/social?fromAdoptionRequest=${requestId}`);
    // Close the modal after navigating
    onClose();
  };

  const confirmAcceptRequest = async () => {
    const requestId = confirmRequestId;
    if (acceptingRequestId) return;
    if (!requestId) return;

    try {
      setAcceptingRequestId(requestId);
      setError(null);

      await adoptionAPI.acceptAdoptionRequest(requestId);

      // Reset fetched tabs to force re-fetch on all tabs to keep counters in sync
      setFetchedTabs(new Set());
      await fetchRequests(activeTab);

      // Notify parent component
      if (onRequestAccepted) {
        onRequestAccepted(requestId);
      }
    } catch (err) {
      console.error('Error accepting adoption request:', err);
      setError(handleApiError(err));
    } finally {
      setAcceptingRequestId(null);
      setConfirmRequestId(null);
    }
  };

  const handleRejectRequest = async (requestId) => {
    if (rejectingRequestId) return;

    try {
      setRejectingRequestId(requestId);
      setError(null);

      await adoptionAPI.rejectAdoptionRequest(requestId);

      // Reset fetched tabs to force re-fetch on all tabs to keep counters in sync
      setFetchedTabs(new Set());
      await fetchRequests(activeTab);

      // Notify parent component to refresh feed
      if (onRequestRejected) {
        onRequestRejected(requestId);
      }
    } catch (err) {
      console.error('Error rejecting adoption request:', err);
      setError(handleApiError(err));
    } finally {
      setRejectingRequestId(null);
    }
  };

  const handleCancelRequest = async (requestId) => {
    if (cancelingRequestId) return;

    try {
      setCancelingRequestId(requestId);
      setError(null);

      await adoptionAPI.cancelAdoptionRequest(requestId);

      // Reset fetched tabs to force re-fetch on all tabs to keep counters in sync
      setFetchedTabs(new Set());
      await fetchRequests(activeTab);
    } catch (err) {
      console.error('Error canceling adoption request:', err);
      setError(handleApiError(err));
    } finally {
      setCancelingRequestId(null);
    }
  };

  const handleClearHistory = async () => {
    if (clearingHistory || !historyRequests.length) return;

    try {
      setClearingHistory(true);
      setError(null);

      await adoptionAPI.clearAdoptionRequestsHistory();

      // Clear the history list in state
      setHistoryRequests([]);
    } catch (err) {
      console.error('Error clearing adoption history:', err);
      setError(handleApiError(err));
    } finally {
      setClearingHistory(null);
      setClearingHistory(false);
    }
  };

  const getPetImage = (pet) => {
    if (!pet) return getUserPlaceholderImage();
    return pet.picture ||
      pet.profile_image_url ||
      pet.profile_image_path ||
      pet.image ||
      pet.avatar ||
      pet.primaryImageUrl ||
      pet.primary_image_url ||
      getUserPlaceholderImage();
  };

  const getPetName = (pet) => {
    if (!pet) return t('adoption.requests.unknownPet') || 'Unknown Pet';
    if (typeof pet.name === 'string') return pet.name;
    if (pet.name?.en) return pet.name.en;
    if (pet.name?.ar) return pet.name.ar;
    return t('adoption.requests.unknownPet') || 'Unknown Pet';
  };

  if (!isOpen) {
    return null;
  }

  const modalContent = (
    <div
      className="adoption-requests-modal-overlay"
      onClick={handleBackdropClick}
      style={{ '--adoption-accent': userTypeColor }}
    >
      <div
        className="adoption-requests-modal"
        onClick={(e) => e.stopPropagation()}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        {/* Header */}
        <div className="adoption-requests-modal-header">
          <h2 className="adoption-requests-modal-title">
            {postId
              ? (t('adoption.requests.titleForPost') || 'Adoption Requests for This Post')
              : (t('adoption.requests.title') || 'Adoption Requests')
            }
          </h2>
          <button
            className="adoption-requests-modal-close"
            onClick={onClose}
            disabled={loading}
          >
            ×
          </button>
        </div>

        {/* Tabs - Only show if no postId filter */}
        {!postId && (
          <div className="adoption-requests-modal-tabs">
            <button
              className={`adoption-requests-tab ${activeTab === 'received' ? 'active' : ''}`}
              onClick={() => setActiveTab('received')}
            >
              {t('adoption.requests.tabs.received') || 'Received'}
              {requests.length > 0 && <span className="tab-count">{requests.length}</span>}
            </button>
            <button
              className={`adoption-requests-tab ${activeTab === 'made' ? 'active' : ''}`}
              onClick={() => setActiveTab('made')}
            >
              {t('adoption.requests.tabs.made') || 'Sent'}
              {madeRequests.length > 0 && <span className="tab-count">{madeRequests.length}</span>}
            </button>
            <button
              className={`adoption-requests-tab ${activeTab === 'history' ? 'active' : ''}`}
              onClick={() => setActiveTab('history')}
            >
              {t('adoption.requests.tabs.history') || 'History'}
            </button>
          </div>
        )}

        {/* Content */}
        <div className="adoption-requests-modal-content">
          {loading ? (
            <div className="adoption-requests-modal-loading">
              <div className="adoption-requests-modal-spinner"></div>
              <p>{t('adoption.requests.loading') || 'Loading requests...'}</p>
            </div>
          ) : error ? (
            <div className="adoption-requests-modal-error">
              <p>{error}</p>
              <button onClick={fetchRequests}>
                {t('adoption.requests.retry') || 'Retry'}
              </button>
            </div>
          ) : (activeTab === 'received' ? requests : activeTab === 'made' ? madeRequests : historyRequests).length === 0 ? (
            <div className="adoption-requests-modal-empty">
              <p>
                {activeTab === 'received'
                  ? (t('adoption.requests.empty') || 'No pending requests received.')
                  : activeTab === 'made'
                    ? (t('adoption.requests.emptyMade') || 'You haven\'t made any adoption requests yet.')
                    : (t('adoption.requests.emptyHistory') || 'Your history is empty.')
                }
              </p>
            </div>
          ) : (
            <div className="adoption-requests-list">
              {activeTab === 'history' && historyRequests.length > 0 && (
                <div className="adoption-history-header">
                  <button
                    className="clear-history-btn"
                    onClick={handleClearHistory}
                    disabled={clearingHistory}
                  >
                    {clearingHistory ? t('common.clearing') || 'Clearing...' : t('adoption.requests.clearHistory') || 'Clear History'}
                  </button>
                </div>
              )}
              {(activeTab === 'received' ? requests : activeTab === 'made' ? madeRequests : historyRequests).map(request => {
                const isMadeRequest = activeTab === 'made' || (activeTab === 'history' && String(request.requester_id || request.user_id) === String(user?.id || user?.user_id));
                const postOwner = (request.adoption_post?.user || request.adoption_post?.owner);
                const pet = request.pet || request.adoption_post?.pet_info?.[0] || request.adoption_post?.pets?.[0];
                const status = (request.status || 'pending').toLowerCase();

                return (
                  <div
                    key={request.id}
                    className={`adoption-request-item ${highlightedRequestId && String(request.id) === String(highlightedRequestId) ? 'adoption-request-highlighted' : ''}`}
                  >
                    {/* Unified Header: Pet and User */}
                    <div className="adoption-request-card-header">
                      {pet && (
                        <div className="adoption-request-pet-summary">
                          <img
                            src={getPetImage(pet)}
                            alt={getPetName(pet)}
                            onError={(e) => {
                              e.target.src = getUserPlaceholderImage();
                            }}
                          />
                          <div className="adoption-request-pet-details">
                            <h4>{getPetName(pet)}</h4>
                            <p>
                              {pet.species && (typeof pet.species === 'string' ? pet.species : pet.species?.name)}
                              {pet.breed && ` • ${typeof pet.breed === 'string' ? pet.breed : pet.breed?.name}`}
                            </p>
                          </div>
                        </div>
                      )}

                      <div className="adoption-request-divider-v"></div>

                      <div className="adoption-request-user-summary">
                        {isMadeRequest && postOwner ? (
                          <>
                            <img
                              src={postOwner.avatar || postOwner.profile_image_url || getUserPlaceholderImage(postOwner)}
                              alt={postOwner.name || postOwner.username}
                              onError={(e) => {
                                e.target.src = getUserPlaceholderImage(postOwner);
                              }}
                            />
                            <div className="adoption-request-user-details">
                              <span className="user-label">{t('adoption.requests.labels.owner') || 'Owner'}</span>
                              <h4>{postOwner.name || postOwner.username}</h4>
                              <button
                                onClick={() => handleViewProfile(postOwner.id || postOwner.user_id)}
                                className="view-profile-mini-btn"
                              >
                                {t('adoption.requests.viewProfile') || 'View Profile'}
                              </button>
                            </div>
                          </>
                        ) : (
                          <>
                            <img
                              src={request.requester?.avatar || getUserPlaceholderImage(request.requester)}
                              alt={request.requester?.name || request.requester?.username}
                              onError={(e) => {
                                e.target.src = getUserPlaceholderImage(request.requester);
                              }}
                            />
                            <div className="adoption-request-user-details">
                              <span className="user-label">{t('adoption.requests.labels.requester') || 'Requester'}</span>
                              <h4>{request.requester?.name || request.requester?.username}</h4>
                              <button
                                onClick={() => handleViewProfile(request.requester.id)}
                                className="view-profile-mini-btn"
                              >
                                {t('adoption.requests.viewProfile') || 'View Profile'}
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {request.message && (
                      <div className="adoption-request-message">
                        <p>"{request.message}"</p>
                      </div>
                    )}

                    {/* Footer: Status and Actions */}
                    <div className="adoption-request-card-footer">
                      <div className="adoption-request-status-info">
                        <span className={`adoption-request-status-badge status-${status}`}>
                          {status === 'pending' && (t('adoption.requests.status.pending') || 'Pending')}
                          {status === 'accepted' && (t('adoption.requests.status.accepted') || 'Accepted')}
                          {status === 'rejected' && (t('adoption.requests.status.rejected') || 'Rejected')}
                        </span>
                        <span className="adoption-request-date-v2">
                          {new Date(request.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      {activeTab !== 'history' && (
                        <div className="adoption-request-actions-v2">
                          {isMadeRequest ? (
                            <>
                              {status === 'pending' && (
                                <button
                                  className="adoption-request-reject-btn-v2"
                                  onClick={() => handleCancelRequest(request.id)}
                                  disabled={!!cancelingRequestId}
                                >
                                  {cancelingRequestId === request.id ? t('common.processing') : t('adoption.requests.cancel') || 'Cancel'}
                                </button>
                              )}
                              <button
                                className="adoption-request-chat-btn-v2"
                                onClick={() => handleStartChat(postOwner?.id || postOwner?.user_id, request.id)}
                              >
                                {t('adoption.requests.chat') || 'Go to Chat'}
                              </button>
                              <button
                                className="adoption-request-secondary-btn"
                                onClick={() => {
                                  setSelectedPostId(request.adoption_post_id);
                                  setShowPostModal(true);
                                }}
                              >
                                {t('adoption.requests.viewPost') || 'View Post'}
                              </button>
                            </>
                          ) : (
                            <>
                              {status === 'pending' ? (
                                <>
                                  <button
                                    className="adoption-request-chat-btn-v2"
                                    onClick={() => handleStartChat(request.requester.id, request.id)}
                                  >
                                    {t('adoption.requests.chat') || 'Chat'}
                                  </button>
                                  <button
                                    className="adoption-request-accept-btn-v2"
                                    onClick={() => setConfirmRequestId(request.id)}
                                    disabled={!!acceptingRequestId}
                                  >
                                    {acceptingRequestId === request.id ? t('common.processing') : t('adoption.requests.accept') || 'Accept'}
                                  </button>
                                  <button
                                    className="adoption-request-reject-btn-v2"
                                    onClick={() => handleRejectRequest(request.id)}
                                    disabled={!!rejectingRequestId}
                                  >
                                    {rejectingRequestId === request.id ? t('common.processing') : t('adoption.requests.reject') || 'Reject'}
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    className="adoption-request-chat-btn-v2"
                                    onClick={() => handleStartChat(request.requester.id, request.id)}
                                  >
                                    {t('adoption.requests.chat') || 'Chat'}
                                  </button>
                                  <button
                                    className="adoption-request-secondary-btn"
                                    onClick={() => {
                                      setSelectedPostId(request.adoption_post_id);
                                      setShowPostModal(true);
                                    }}
                                  >
                                    {t('adoption.requests.viewPost') || 'View Post'}
                                  </button>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Error Message */}
          {error && requests.length > 0 && (
            <div className="adoption-requests-modal-error-message">
              {error}
            </div>
          )}
        </div>

        {/* Accept confirmation overlay */}
        {confirmRequestId && (
          <div className="adoption-request-confirm-overlay">
            <div className="adoption-request-confirm-modal">
              <h3 className="adoption-request-confirm-title">
                {t('adoption.requests.confirmAcceptTitle') || 'Confirm Adoption Transfer'}
              </h3>
              <p className="adoption-request-confirm-message">
                {t('adoption.requests.confirmAcceptMessage') ||
                  'By accepting this request, this pet will be removed from your profile and all of its medical and adoption data will be transferred to the adopter. This action cannot be undone.'}
              </p>
              <div className="adoption-request-confirm-disclaimer">
                <p className="adoption-request-confirm-disclaimer-text">
                  {t('adoption.requests.confirmAcceptDisclaimer') ||
                    '⚠️ Important: Sulhafa is not responsible for any follow-up actions or outcomes after the guardianship transfer. Please double-check all details about the adopter and be 100% certain about this transfer before proceeding. Once completed, Sulhafa cannot assist with disputes or issues that may arise from this transfer.'}
                </p>
              </div>
              <div className="adoption-request-confirm-actions">
                <button
                  type="button"
                  className="adoption-request-confirm-secondary"
                  onClick={() => setConfirmRequestId(null)}
                  disabled={!!acceptingRequestId}
                >
                  {t('adoption.requests.confirmCancel') || 'No, nevermind'}
                </button>
                <button
                  type="button"
                  className="adoption-request-confirm-primary"
                  onClick={confirmAcceptRequest}
                  disabled={!!acceptingRequestId}
                >
                  {acceptingRequestId
                    ? (t('adoption.requests.accepting') || 'Accepting...')
                    : (t('adoption.requests.confirmAccept') || 'Yes, I understand')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {createPortal(modalContent, document.body)}
      <AdoptionPostDetailModal
        isOpen={showPostModal}
        onClose={() => {
          setShowPostModal(false);
          setSelectedPostId(null);
        }}
        postId={selectedPostId}
        currentUser={currentUser}
      />
    </>
  );
};

export default AdoptionRequestsModal;

